import { ChevronLeft, ChevronRight, CalendarDays, Loader2 } from 'lucide-react';

interface PeriodStepperProps {
  /** "12 – 18 May 2026" / "August 2026" */
  label: string;
  /** "This week" / "Last month" / "3 months ago" */
  relative: string;
  /** Period noun for titles and aria-labels — "week" / "month". */
  unit: 'week' | 'month';
  offset: number;
  loading?: boolean;
  onOffsetChange: (next: number) => void;
}

/**
 * ‹ label · relative › selector shared by the Current Week and Current Month
 * panels. The next button is disabled at offset 0 (the live period); a "Jump
 * to this …" link appears once the user has stepped back.
 */
export function PeriodStepper({ label, relative, unit, offset, loading, onOffsetChange }: PeriodStepperProps) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid={`${unit}-stepper`}>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onOffsetChange(offset - 1)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"
          title={`Previous ${unit}`}
          aria-label={`Previous ${unit}`}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-2 px-1">
          <CalendarDays className="h-4 w-4 text-slate-400" />
          <span className="text-sm font-semibold text-slate-800 tabular-nums">{label}</span>
          <span className="text-[11px] font-medium text-slate-400">{relative}</span>
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
        </div>
        <button
          type="button"
          onClick={() => onOffsetChange(Math.min(0, offset + 1))}
          disabled={offset >= 0}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          title={`Next ${unit}`}
          aria-label={`Next ${unit}`}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      {offset !== 0 && (
        <button
          type="button"
          onClick={() => onOffsetChange(0)}
          className="text-xs font-medium text-slate-600 underline-offset-2 hover:text-slate-900 hover:underline"
        >
          Jump to this {unit}
        </button>
      )}
    </div>
  );
}

/** "This month" / "Last month" / "N months ago" (and the week equivalents). */
export function relativePeriodLabel(offset: number, unit: 'week' | 'month'): string {
  if (offset === 0) return `This ${unit}`;
  if (offset === -1) return `Last ${unit}`;
  return `${Math.abs(offset)} ${unit}s ago`;
}
